import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { Stack } from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import GeneralService from '../../../../../../services/general';
import { IIncomeInvoiceResourceShortProps } from '../../../../../../props/finance/incomes/invoices/invoice';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

type IncomeInvoiceStatusPieChartProps = {
    invoices?: IIncomeInvoiceResourceShortProps[];
}

const COLORS = {
    paid: '#218d21',
    unpaid: '#ae383e',
    pending: '#d79e1a'
};

const IncomeInvoiceStatusPieChart: React.FC<IncomeInvoiceStatusPieChartProps> = (props: IncomeInvoiceStatusPieChartProps) => {
    const [chartData, setChartData] = React.useState<{ name: string; value: number; count: number; color: string }[]>([]);
    const [totals, setTotals] = React.useState<{ totalPaid: number; totalUnpaid: number; totalCount: number }>({
        totalPaid: 0,
        totalUnpaid: 0,
        totalCount: 0
    });

    useEffect(() => {
        calculateData();
    }, [props.invoices]);

    const calculateData = () => {
        let paidAmt = 0;
        let unpaidAmt = 0;
        let paidCount = 0;
        let unpaidCount = 0;

        (props.invoices || []).forEach((inv) => {
            const p = Number(inv.paid || '0');
            const u = Number(inv.unpaid || '0');
            paidAmt += p;
            unpaidAmt += u;

            if (u <= 0 && p > 0) {
                paidCount++;
            } else {
                unpaidCount++;
            }
        });

        const data = [
            { name: 'Terbayar (Paid)', value: paidAmt, count: paidCount, color: COLORS.paid },
            { name: 'Belum Dibayar (Unpaid)', value: unpaidAmt, count: unpaidCount, color: COLORS.unpaid }
        ].filter(d => d.value > 0 || d.count > 0);

        setChartData(data);
        setTotals({
            totalPaid: paidAmt,
            totalUnpaid: unpaidAmt,
            totalCount: (props.invoices || []).length
        });
    };

    const renderTooltip = (props: any) => {
        if (props.active && props.payload && props.payload.length) {
            const item = props.payload[0];
            const dataObj = item.payload;

            return <Stack tokens={{ childrenGap: 6 }} className={styles.tooltip}>
                <Label size={'small'}>{dataObj.name}</Label>
                <Stack className='divider'></Stack>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign='space-between' verticalAlign='center'>
                        <Text size={'xsmall'}>Nominal</Text>
                        <Text size={'small'} style={{ fontWeight: 600 }}>Rp. {GeneralService.getNumberWithSeparator(dataObj.value)}</Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign='space-between' verticalAlign='center'>
                        <Text size={'xsmall'}>Jumlah Invoice</Text>
                        <Text size={'small'}>{dataObj.count} invoice</Text>
                    </Stack>
                </Stack>
            </Stack>;
        }
        return null;
    };

    const grandTotal = totals.totalPaid + totals.totalUnpaid;
    const paidPercentage = grandTotal > 0 ? Math.round((totals.totalPaid / grandTotal) * 100) : 0;
    const unpaidPercentage = grandTotal > 0 ? 100 - paidPercentage : 0;

    return <Stack className={styles.container} tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }}>
        <Label size={'medium'}>Rasio Invoice Terbayar vs Belum Dibayar</Label>
        <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign='center' styles={{ root: { width: '100%', height: 320 } }}>
            <Stack styles={{ root: { width: '55%', height: '100%' } }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={85}
                            paddingAngle={4}
                            dataKey="value"
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip content={renderTooltip} />
                    </PieChart>
                </ResponsiveContainer>
            </Stack>
            <Stack tokens={{ childrenGap: 12 }} styles={{ root: { width: '45%' } }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign='center'>
                        <Stack.Item className={styles.indicator} styles={{ root: { backgroundColor: COLORS.paid } }}> </Stack.Item>
                        <Label size={'xsmall'}>Terbayar (Paid)</Label>
                    </Stack>
                    <Text size={'medium'} className='color-green' style={{ fontWeight: 600 }}>
                        Rp. {GeneralService.getNumberWithSeparator(totals.totalPaid)} ({paidPercentage}%)
                    </Text>
                </Stack>
                <Stack className='divider'></Stack>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign='center'>
                        <Stack.Item className={styles.indicator} styles={{ root: { backgroundColor: COLORS.unpaid } }}> </Stack.Item>
                        <Label size={'xsmall'}>Belum Dibayar (Unpaid)</Label>
                    </Stack>
                    <Text size={'medium'} className='color-red' style={{ fontWeight: 600 }}>
                        Rp. {GeneralService.getNumberWithSeparator(totals.totalUnpaid)} ({unpaidPercentage}%)
                    </Text>
                </Stack>
            </Stack>
        </Stack>
    </Stack>;
};

export default IncomeInvoiceStatusPieChart;
